import { useEffect, useRef, useState } from "react";
import { api, ApiError, type AdminService } from "../lib/api.ts";
import {
  ACCEPTED_IMAGE_TYPES,
  formatBytes,
  ImagePrepError,
  loadCropSource,
  renderCrop,
  type CropRect,
  type CropSource,
} from "../lib/image.ts";
import { ImageCropper } from "./ImageCropper.tsx";

interface TreatmentImageFieldProps {
  token: string;
  service: AdminService;
  storeConfigured: boolean;
  onChanged: () => void | Promise<void>;
  onUnauthorised: () => void;
}

/**
 * Upload, replace and remove a treatment photograph.
 *
 * Only offered for a treatment that already exists, because the upload targets
 * its id - a new treatment is saved first, then given a photograph.
 *
 * Choosing a file does not upload it: it opens the cropper, because the stored
 * photograph is a fixed landscape shape and the studio should be the one to say
 * which part of a portrait phone photograph survives that.
 */
export function TreatmentImageField({
  token,
  service,
  storeConfigured,
  onChanged,
  onUnauthorised,
}: TreatmentImageFieldProps) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [altText, setAltText] = useState(service.imageAltText ?? "");
  const [source, setSource] = useState<CropSource | null>(null);

  const inputId = `image-file-${service.id}`;

  // The working copy holds a full-size canvas and an object URL, so it is
  // released the moment it is replaced or the field goes away.
  useEffect(() => () => source?.release(), [source]);

  /** Decode the chosen file and hand it to the cropper. Nothing is sent yet. */
  async function choose(file: File | undefined) {
    if (!file) return;
    setBusy(true);
    setError(null);
    setNote(null);

    try {
      setSource(await loadCropSource(file));
    } catch (caught) {
      setError(
        caught instanceof ImagePrepError
          ? caught.message
          : "That image could not be read.",
      );
    } finally {
      setBusy(false);
      // Clear the input so choosing the same file again still fires a change.
      if (fileInput.current) fileInput.current.value = "";
    }
  }

  /** Cut the chosen rectangle out of the working copy and upload just that. */
  async function upload(rect: CropRect) {
    if (!source) return;
    setBusy(true);
    setError(null);
    setNote(null);

    try {
      const prepared = await renderCrop(source, rect);
      const description = altText.trim() || service.name;

      const result = await api.admin.uploadServiceImage(
        token,
        service.id,
        prepared.blob,
        prepared.contentType,
        description,
      );

      setAltText(result.imageAltText);
      setNote(
        `Uploaded at ${prepared.width}×${prepared.height}, ` +
          `${formatBytes(result.bytes)} (from ${formatBytes(prepared.originalBytes)}).`,
      );
      setSource(null);
      await onChanged();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401)
        return onUnauthorised();
      // The cropper stays open on failure, so the framing is not lost.
      if (caught instanceof ImagePrepError) setError(caught.message);
      else
        setError(
          caught instanceof ApiError
            ? caught.message
            : "The image could not be uploaded.",
        );
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm(`Remove the photograph from ${service.name}?`)) return;
    setBusy(true);
    setError(null);
    setNote(null);
    try {
      await api.admin.deleteServiceImage(token, service.id);
      setAltText("");
      setNote("Photograph removed.");
      await onChanged();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401)
        return onUnauthorised();
      setError(
        caught instanceof ApiError
          ? caught.message
          : "The image could not be removed.",
      );
    } finally {
      setBusy(false);
    }
  }

  async function saveAlt() {
    setBusy(true);
    setError(null);
    try {
      await api.admin.updateServiceImageAlt(token, service.id, altText.trim());
      setNote("Description saved.");
      await onChanged();
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401)
        return onUnauthorised();
      setError(
        caught instanceof ApiError
          ? caught.message
          : "That could not be saved.",
      );
    } finally {
      setBusy(false);
    }
  }

  if (!storeConfigured) {
    return (
      <div className="field">
        <span className="label">Photograph</span>
        <p className="notice">
          Image storage is not configured on the API, so photographs cannot be
          uploaded yet. Set AZURE_STORAGE_ACCOUNT in the App Service settings.
        </p>
      </div>
    );
  }

  return (
    <div className="field">
      <span className="label">Photograph</span>

      <div className="image-field">
        <div className="image-field__preview">
          {service.imageUrl ? (
            <img
              src={service.imageUrl}
              alt={service.imageAltText ?? service.name}
            />
          ) : (
            <p className="image-field__empty">
              No photograph. The website shows a line drawing instead.
            </p>
          )}
        </div>

        <div className="image-field__controls">
          <div className="image-field__actions">
            <input
              ref={fileInput}
              id={inputId}
              className="image-field__file"
              type="file"
              accept={ACCEPTED_IMAGE_TYPES.join(",")}
              disabled={busy}
              onChange={(event) => void choose(event.target.files?.[0])}
            />
            <label htmlFor={inputId} className="image-field__label">
              {busy
                ? "Working…"
                : service.imageUrl
                  ? "Replace photo"
                  : "Upload photo"}
            </label>

            {service.imageUrl && (
              <button
                type="button"
                className="btn btn--danger"
                disabled={busy}
                onClick={remove}
              >
                Remove
              </button>
            )}
          </div>

          <div className="field">
            <label htmlFor={`alt-${service.id}`}>Describe the photo</label>
            <input
              id={`alt-${service.id}`}
              className="input"
              value={altText}
              maxLength={200}
              placeholder={service.name}
              disabled={busy}
              onChange={(event) => setAltText(event.target.value)}
            />
            <p className="field-hint">
              Read aloud by screen readers, and shown if the image fails to
              load. Describe what is in the picture, not the treatment name.
            </p>
            {service.imageUrl &&
              altText.trim() !== (service.imageAltText ?? "") && (
                <button
                  type="button"
                  className="btn btn--quiet"
                  disabled={busy}
                  onClick={saveAlt}
                >
                  Save description
                </button>
              )}
          </div>

          <p className="field-hint">
            JPEG, PNG, WebP or AVIF. Large photographs are scaled down in your
            browser before uploading, so there is no need to resize them first -
            you choose which part of the picture is kept.
          </p>

          {note && <p className="notice notice--success">{note}</p>}
          {error && !source && <p className="notice notice--error">{error}</p>}
        </div>
      </div>

      {source && (
        <ImageCropper
          source={source}
          subject={service.name}
          busy={busy}
          error={error}
          onCancel={() => {
            setSource(null);
            setError(null);
          }}
          onConfirm={(rect) => void upload(rect)}
        />
      )}
    </div>
  );
}
